import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { Button, LinkButton } from "../../../../../../components/Button";
import * as Fonts from "../../../../../../components/Fonts";
import { FreeKeeposLimitModal } from "../../../../../../components/FreeKeeposLimitModal";
import { KeepoLoader } from "../../../../../../components/KeepoLoader";
import { FreeTag, PlusTag } from "../../../../../../components/PlusTag";
import { Container as BarContainer, Box as BarBox, StickySpacer } from "../../../../../../components/SaveBar/styles";
import { ScreenHeader } from "../../../../../../components/SubHeader";
import constants from "../../../../../../constants";
import { useAppState } from "../../../../../../contexts/App";
import { usePreviewSite } from "../../../../../../contexts/PreviewSite";
import { useSite } from "../../../../../../contexts/Site";
import { getPages, siteHasAgendaPage } from "../../../../../../model/pages";
import { siteUsesPlusStyle } from "../../../../../../model/plusFeatures";
import { siteHasActivePlusSections } from "../../../../../../model/sectionDefinitions";
import { Site } from "../../../../../../model/site";
import { keepoInternalAnalyticsService } from "../../../../../../services/analytics";
import { api, hasActiveSubscription } from "../../../../../../services/api";
import { ERR_FREE_SITES_LIMIT_REACHED, isApiError } from "../../../../../../services/api/errors";
import { customDomainService } from "../../../../../../services/customDomain";
import { publishSite } from "../../../../../../services/publishSite";
import CreationProgress from "../../../../components/CreationProgress";
import { Card, Container, Section } from "../../../Title/styles";
import { FireButton, Note, PaidBanner, Status, SummaryRow } from "./styles";

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const GlobeIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </svg>
);

const LayersIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3 3 8l9 5 9-5-9-5z" />
    <path d="m3 13 9 5 9-5" />
  </svg>
);

const PlanIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" />
  </svg>
);

const CheckIcon = () => (
  <svg {...iconProps}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

/** Arriving right after subscribing (from the success screen): paid, or a boleto generated and not paid yet. */
export type JustPaid = "paid" | "boleto";

/**
 * The last step of creating or editing a Keepo: a summary of what goes on the air, and the only
 * place that publishes it. Reached after the address (new Keepo) or the style (published one), and
 * after the PLUS page for FREE Keepos (subscribed, or going on as FREE).
 */
const PublishReadyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prefix = "pages.editor.publish.ready";

  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();
  // Only in the navigation's state: reloading, or coming back here another way, doesn't show it again
  const justPaid = (useLocation().state as { justPaid?: JustPaid } | null)?.justPaid;

  const [isPublishing, setPublishing] = useState(false);
  const [showFreeKeeposLimitModal, setShowFreeKeeposLimitModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  // "Publicando…", "Colocando no ar…": one after another while it publishes
  const publishingSteps = t(`${prefix}.publishing`, { returnObjects: true }) as string[];
  const [publishingStep, setPublishingStep] = useState(0);
  useEffect(() => {
    if (!isPublishing) {
      setPublishingStep(0);
      return;
    }
    const timer = setInterval(() => setPublishingStep(step => step + 1), 1600);
    return () => clearInterval(timer);
  }, [isPublishing]);

  const isPlus = hasActiveSubscription(appState.editingSiteSubscription);
  const isEditing = !!appState.published;
  const address = `${site?.url}.${constants.nameSite}`;
  const activeSections = site?.sections?.filter(({ active }) => active).length || 0;
  const backTo = isEditing ? "/editor/style?flow=publish" : "/editor/publish";
  const backLabel = t(`${prefix}.back.${isEditing ? "edit" : "create"}`);
  // the pages that go on the air with it (PLUS): all of them, as one out of the menu is still at its address
  const activePages = isPlus ? getPages(site).length : 0;
  const hasPages = isPlus && getPages(site).length > 0;

  // The preview shows the address it goes on the air at, as in a browser's address bar
  const { setPreviewAddress } = usePreviewSite();
  useEffect(() => {
    setPreviewAddress(site?.url ? address : undefined);
    return () => setPreviewAddress(undefined);
  }, [address, site?.url, setPreviewAddress]);

  // A connected custom domain (PLUS) is the Keepo's other address, shown under the keepo.bio one
  const [customDomain, setCustomDomain] = useState<string>();
  useEffect(() => {
    if (!isPlus || !appState.editingSiteId) {
      return;
    }
    let cancelled = false;
    customDomainService
      .get(appState.editingSiteId)
      .then(domain => !cancelled && setCustomDomain(domain?.status === "connected" ? domain.domain : undefined))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [appState.editingSiteId, isPlus]);

  // Opened with no Keepo being edited (e.g. reloaded): nothing to publish here. A FREE Keepo with
  // active PLUS sections can't go on the air as it is: back to its sections, which say why
  useEffect(() => {
    if (!appState.editingSiteId || !site?.url) {
      navigate("/sites", { replace: true });
    } else if (!isPlus && siteHasActivePlusSections(site)) {
      navigate("/editor/sections?plusRequired=1", { replace: true });
    } else if (!isPlus && siteHasAgendaPage(site)) {
      // the Agenda is PLUS: its page goes, or the Keepo goes PLUS (Páginas says why)
      navigate("/editor/pages?plusRequired=agenda", { replace: true });
    } else if (!isPlus && !appState.published && siteUsesPlusStyle(site)) {
      // the look's PLUS items, free to try while creating: the simple version first (Personalizar says why)
      navigate("/editor/style?flow=publish&plusRequired=1", { replace: true });
    }
    // Only on arrival: once here, publishing changes the state this checks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const publishHandler = async () => {
    setPublishing(true);
    setErrorMessage(undefined);

    try {
      const publishedSite = await publishSite(
        appState.editingSiteId as string,
        site as Site,
        appState.editingSiteSubscription,
        appState.editingSiteDisabled,
        appState.published
      );
      // what's on the air is what's being edited: the draft the publication replaced is gone, nothing is pending
      setAppState({ ...appState, published: true, editingSiteDisabled: false, editingSiteModified: false });
      setSite(publishedSite);
      api
        .getUser()
        .then(user =>
          keepoInternalAnalyticsService.notifySitePublishedEvent(user, appState.editingSiteId as string, !isEditing)
        )
        .catch(() => undefined);
      navigate(`/editor/publish/success?first=${!isEditing}`);
    } catch (e) {
      if (isApiError(e, ERR_FREE_SITES_LIMIT_REACHED)) {
        setShowFreeKeeposLimitModal(true);
      } else {
        setErrorMessage(t("pages.editor.publish.errors.generic"));
      }
      setPublishing(false);
    }
  };

  return (
    <Container>
      {showFreeKeeposLimitModal && (
        <FreeKeeposLimitModal
          i18nKeyPrefix="pages.editor.publish.freeKeeposLimitReached"
          onConfirm={() => navigate(`/subscription/features?site=${appState.editingSiteId}`)}
          onCancel={() => setShowFreeKeeposLimitModal(false)}
        />
      )}
      {/* where the person is in the flow, on top; the screen's own header under it */}
      <CreationProgress step="ready" />
      <ScreenHeader title={t(`${prefix}.title`)} back={{ label: backLabel, to: backTo }} />
      <Section>
        {justPaid && (
          <PaidBanner role="status">
            <span className="icon">
              <CheckIcon />
            </span>
            <span className="text">
              <b>{t(`${prefix}.justPaid.${justPaid}.title`)}</b>
              <span>{t(`${prefix}.justPaid.${justPaid}.description`)}</span>
            </span>
          </PaidBanner>
        )}
        <Fonts.Label>{t(`${prefix}.label`)}</Fonts.Label>
        <Card>
          <SummaryRow>
            <span className="icon">
              <GlobeIcon />
            </span>
            <span className="text">
              <span className="name">{t(`${prefix}.address`)}</span>
              <span className="value">
                <span className="address">{address}</span>
              </span>
              {customDomain && (
                <span className="value">
                  <span className="address">{customDomain}</span>
                </span>
              )}
            </span>
            <Status $online={isEditing}>{t(`${prefix}.status.${isEditing ? "online" : "offline"}`)}</Status>
          </SummaryRow>
          <SummaryRow>
            <span className="icon">
              <LayersIcon />
            </span>
            <span className="text">
              <span className="name">{t(`${prefix}.sections`)}</span>
              <span className="value">{t(`${prefix}.activeSections`, { count: activeSections })}</span>
            </span>
          </SummaryRow>
          {hasPages && (
            <SummaryRow>
              <span className="icon">
                <LayersIcon />
              </span>
              <span className="text">
                <span className="name">{t(`${prefix}.pages`)}</span>
                <span className="value">
                  {activePages === 1
                    ? t(`${prefix}.activePagesOne`)
                    : t(`${prefix}.activePagesMany`, { count: activePages })}
                </span>
              </span>
            </SummaryRow>
          )}
          <SummaryRow>
            <span className="icon">
              <PlanIcon />
            </span>
            <span className="text">
              <span className="name">{t(`${prefix}.plan`)}</span>
              <span className="value">
                Keepo {isPlus ? <PlusTag /> : <FreeTag>{t("pages.keepoDashboard.plan.free")}</FreeTag>}
              </span>
            </span>
          </SummaryRow>
          {errorMessage && <p className="error">{errorMessage}</p>}
        </Card>
        <Note>{t(`${prefix}.note.${isEditing ? "edit" : "create"}`)}</Note>
      </Section>

      {/* Same fixed bar as the other steps */}
      <StickySpacer />
      <BarBox $sticky>
        <BarContainer>
          <LinkButton onClick={() => navigate(backTo)}>{t("pages.editor.templates.back")}</LinkButton>
          <FireButton>
            <span className="fire-halo" />
            <span className="fire-ring" />
            <Button
              type="INFO"
              size="LG"
              disabled={isPublishing}
              aria-busy={isPublishing}
              onClick={() => !isPublishing && publishHandler()}>
              {/* The fire, and while publishing the Keepo symbol breathing with what's going on */}
              {isPublishing ? (
                <KeepoLoader className="symbol" size={13} />
              ) : (
                <span className="symbol fire" aria-hidden>
                  🔥
                </span>
              )}
              <span aria-live="polite">
                {isPublishing
                  ? publishingSteps[publishingStep % publishingSteps.length]
                  : t(`${prefix}.button.${isEditing ? "edit" : "create"}`)}
              </span>
            </Button>
          </FireButton>
        </BarContainer>
      </BarBox>
    </Container>
  );
};

export default PublishReadyPage;
